import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import usePositionsState, { FilterState } from '../trade-monitor/usePageState';
import { useDebouncedState } from 'utils/useDebounce';
import { isValidAddress } from 'helpers/api/web3Utils';
import { FullScreenModal } from '../components/modal/FullScreenModal';
import TextInput from '../components/inputs/text-input/text-input';
import { IconButton } from 'modules/shared-components/button/IconButton';
import { useSelectedChain } from '../components/chains/getChains';

export function SnipesFilters({ close }: { close: () => void }) {
  const { filtersState, setFilters } = usePositionsState();
  const updateFilters = (update: Partial<FilterState>) => {
    setFilters({ ...filtersState, ...update });
  };

  const [tokenAddress, debouncedTokenAddress, setTokenAddress] =
    useDebouncedState('', 250);
  const [botId, debouncedBotId, setBotId] = useDebouncedState('', 250);
  const selectedChain = useSelectedChain();
  const [addressError, setAddressError] = useState(false);
  const [botIdError, setBotIdError] = useState(false);

  useEffect(() => {
    if (debouncedTokenAddress) {
      setAddressError(!isValidAddress(debouncedTokenAddress, selectedChain));
      if (isValidAddress(debouncedTokenAddress, selectedChain)) {
        updateFilters({
          tokenAddress: debouncedTokenAddress,
        });
      }
    }
  }, [debouncedTokenAddress]);

  function isValidUUIDv4(uuid: string) {
    const uuidv4Pattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidv4Pattern.test(uuid);
  }

  useEffect(() => {
    if (debouncedBotId) {
      setBotIdError(!isValidUUIDv4(debouncedBotId));
      if (isValidUUIDv4(debouncedBotId)) {
        updateFilters({
          botId: debouncedBotId,
        });
      }
    }
  }, [debouncedBotId]);

  const handleTokenAddressInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();

    const inputValue = e.target.value;
    setTokenAddress(inputValue);
    if (!inputValue) {
      setAddressError(false);
      updateFilters({
        tokenAddress: undefined,
      });
    }
  };
  const handleBotIdInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const inputValue = e.target.value;
    setBotId(inputValue);
    if (!inputValue) {
      setBotIdError(false);
      updateFilters({
        botId: undefined,
      });
    }
  };

  return (
    <FullScreenModal maxWidth="max-w-[500px]" close={close}>
      <div className="xxs:px-2 lg:p-0">
        <div className="xxs:flex xxs:justify-end lg:hidden">
          <div className="xxs:mt-4 xxs:mb-2 sticky top-2">
            <IconButton
              onClick={close}
              className="z-50 ml-auto xxs:rounded xxs:text-dex-white-secondary hover:text-dex-white xxs:bg-dex-black-700 hover:bg-dex-black-600 xxs:p-1 "
            >
              <XMarkIcon className="w-6" />
            </IconButton>
          </div>
        </div>
        <div className="h-full flex flex-col gap-1" aria-hidden="true">
          <div>
            <TextInput
              name="tokenAddress"
              error={addressError ? 'Address not valid' : ''}
              value={
                tokenAddress === '' ? filtersState.tokenAddress : tokenAddress
              }
              prefix={
                <MagnifyingGlassIcon className="w-4 text-dex-white-secondary" />
              }
              onChange={handleTokenAddressInputChange}
              placeholder="Token Address"
            />
          </div>
          <div>
            <TextInput
              name="botId"
              value={botId}
              error={botIdError ? 'Bot format not valid' : ''}
              prefix={
                <MagnifyingGlassIcon className="w-4 text-dex-white-secondary" />
              }
              onChange={handleBotIdInputChange}
              placeholder="Bot Id"
            />
          </div>
        </div>
      </div>
    </FullScreenModal>
  );
}
